import type { Database } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SavedDealRow = Database['public']['Tables']['saved_deals']['Row'];

export type DealPhotoJoin = { url: string; alt_text: string | null; position: number };

type DealJoin = Pick<
  Database['public']['Tables']['deals']['Row'],
  | 'id'
  | 'type'
  | 'title'
  | 'slug'
  | 'reference_id'
  | 'zone'
  | 'community'
  | 'building'
  | 'bedrooms'
  | 'area_sqft'
  | 'asking_price'
  | 'original_price'
  | 'discount_pct'
  | 'dld_confidence'
  | 'dld_scope'
  | 'created_at'
> & {
  // Embedded cover/gallery photos (Spec §M7 L998 — Saved cards identical to the
  // catalogue). PostgREST returns an array (possibly empty); optional here so
  // older fixtures stay valid. Sorted + cover-picked by mapSavedRowToDealView.
  deal_photos?: DealPhotoJoin[];
};

export type SavedDealRowJoined = SavedDealRow & {
  deals: DealJoin | null;
};

export type SavedDealsSort = 'date' | 'discount' | 'price';

// ---------------------------------------------------------------------------
// Custom error
// ---------------------------------------------------------------------------
// The DB trigger `saved_deals_enforce_max_trg` (20260506150000_buyer_engagement.sql
// lines 109-136) raises check_violation 23514 with the literal message
// 'saved_deals: maximum 500 favourites per user (Spec V1.1 §M7 line 1090).'
// when a 501st INSERT is attempted. We surface that as a typed error so the
// UI can show the localised toast `buyer.saved.errors.capReached` (Plan
// arbitrage C). Any other 23514 is propagated verbatim.

export const SAVED_DEALS_CAP = 500;
const CAP_ERROR_MESSAGE_FRAGMENT = 'saved_deals: maximum 500';

export class SavedDealCapError extends Error {
  constructor(message?: string) {
    super(message ?? 'saved_deals_cap_reached');
    this.name = 'SavedDealCapError';
  }
}

function isSavedDealsCapError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; message?: unknown };
  return (
    e.code === '23514' &&
    typeof e.message === 'string' &&
    e.message.includes(CAP_ERROR_MESSAGE_FRAGMENT)
  );
}

// ---------------------------------------------------------------------------
// Select shape (used by both list + toggle helpers)
// ---------------------------------------------------------------------------

const DEAL_JOIN_COLUMNS =
  'id, type, title, slug, reference_id, zone, community, building, bedrooms, area_sqft, asking_price, original_price, discount_pct, dld_confidence, dld_scope, created_at';

const SELECT_WITH_DEAL = `*, deals:deal_id (${DEAL_JOIN_COLUMNS}, deal_photos(url, alt_text, position))`;

// ---------------------------------------------------------------------------
// listSavedDeals — read favourites with embedded deal info
// ---------------------------------------------------------------------------
// RLS: relies on `saved_deals_buyer_self` (20260506150000 lines 407-417) —
// auth.uid() must match user_id AND `is_email_verified()=true`. The policy
// hides every other user's row, including for super_admin (Matrice §M7
// line 261 — strict buyer scope).
//
// Sort:
//   - 'date'      → SQL ORDER BY created_at DESC (most recent first).
//   - 'discount'  → JS sort on deals.discount_pct DESC (NULL last).
//   - 'price'     → JS sort on deals.asking_price ASC (NULL last).
//
// Deals whose FK target was hard-deleted leave `deals: null`. We drop those
// rows from the result (orphan favourite — soft-delete keeps the row but
// hard-delete CASCADEd the favourite away already, so a NULL here is rare).

export async function listSavedDeals(
  sort: SavedDealsSort = 'date',
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<SavedDealRowJoined[]> {
  const { data, error } = await client
    .from('saved_deals')
    .select(SELECT_WITH_DEAL)
    .order('created_at', { ascending: false });

  if (error) throw error;
  const rows = (data ?? []) as unknown as SavedDealRowJoined[];

  if (sort === 'date') return rows;

  return [...rows].sort((a, b) => {
    if (sort === 'discount') {
      const av = a.deals?.discount_pct ?? -Infinity;
      const bv = b.deals?.discount_pct ?? -Infinity;
      return bv - av;
    }
    // price: ascending (cheapest first), NULL last.
    const av = a.deals?.asking_price ?? Infinity;
    const bv = b.deals?.asking_price ?? Infinity;
    return Number(av) - Number(bv);
  });
}

// ---------------------------------------------------------------------------
// addSavedDeal — INSERT (user_id derived from auth.uid via RLS WITH CHECK)
// ---------------------------------------------------------------------------
// Sends `user_id: undefined`-equivalent shape: the RLS WITH CHECK requires
// `user_id = auth.uid()`, but PostgREST forwards an explicit value when we
// supply one. We rely on a DB-side default-via-RLS pattern: provide auth.uid()
// explicitly from the client too — the policy validates equality.
//
// Errors:
//   - cap 500 trigger → SavedDealCapError (typed for i18n)
//   - unique violation 23505 (deal already saved) → swallow silently and
//     resolve `null` (idempotent from the UI's standpoint).
//   - any other → re-throw verbatim.

export async function addSavedDeal(
  params: { userId: string; dealId: string },
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<SavedDealRow | null> {
  const { data, error } = await client
    .from('saved_deals')
    .insert({ user_id: params.userId, deal_id: params.dealId })
    .select('*')
    .single();

  if (error) {
    if (isSavedDealsCapError(error)) {
      throw new SavedDealCapError((error as { message?: string }).message);
    }
    if ((error as { code?: string }).code === '23505') {
      return null;
    }
    throw error;
  }
  return data as SavedDealRow;
}

// ---------------------------------------------------------------------------
// removeSavedDeal — DELETE filtered by deal_id (RLS scopes user_id)
// ---------------------------------------------------------------------------
// We pass `user_id` explicitly even though the RLS USING clause filters it,
// so a hypothetical client misconfiguration cannot drop someone else's row.

export async function removeSavedDeal(
  params: { userId: string; dealId: string },
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client
    .from('saved_deals')
    .delete()
    .eq('user_id', params.userId)
    .eq('deal_id', params.dealId);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// isDealSaved — head:true query, returns boolean
// ---------------------------------------------------------------------------

export async function isDealSaved(
  params: { userId: string; dealId: string },
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<boolean> {
  const { count, error } = await client
    .from('saved_deals')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', params.userId)
    .eq('deal_id', params.dealId);

  if (error) throw error;
  return (count ?? 0) > 0;
}

// ---------------------------------------------------------------------------
// countSavedDeals — head:true count for cap-reached warning banner
// ---------------------------------------------------------------------------

export async function countSavedDeals(
  userId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<number> {
  const { count, error } = await client
    .from('saved_deals')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);

  if (error) throw error;
  return count ?? 0;
}
